import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Input,
  Tooltip,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  message,
} from "antd";
import {
  SearchOutlined,
  CloseCircleOutlined,
  SettingOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
  UserOutlined,
  CheckCircleOutlined,
  AuditOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService } from "@iso/services";
import IncidentPage from "./Incident.styles";
import IntlMessages from "@iso/components/utility/intlMessages";
import IncidentSettingsModal from "./IncidentSettingsModal";
import moment from "moment";
import { TYPES, STATUSES, INVESTIGATION_TYPES, findIncidentTypeOption, DEFAULT_MANUAL_NOTIFICATION_RULES } from '@iso/constants/incidentOptions';
import { readBlobErrorMessage } from '@iso/lib/helpers/fileDownload';
import styled from 'styled-components';

import useIncidentActions from "./shared/useIncidentActions";
import IncidentActionModals from "./shared/IncidentActionModals";
import { getActionColumn, getApprovalColumn, formatApprovalStatus } from "./shared/incidentActionColumns";

const BRAND_COLOR = '#0B2A1B';

const SettingsIconButton = styled(Button)`
  border-color: ${BRAND_COLOR};
  color: ${BRAND_COLOR};
  &:hover,
  &:focus {
    color: ${BRAND_COLOR};
    border-color: ${BRAND_COLOR};
    background: #f0f5f2;
  }
`;

const DEFAULT_SETTINGS = {
  incident_manager_ids: [],
  notification_rules: [],
};

const normalizeSettings = (settings) => {
  if (!settings || typeof settings !== 'object') {
    return { ...DEFAULT_SETTINGS };
  }

  const managers = Array.isArray(settings.incident_manager_ids)
    ? settings.incident_manager_ids.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
    : [];

  const rules = Array.isArray(settings.notification_rules)
    ? settings.notification_rules
        .map((rule) => ({
          type: rule?.type || null,
          user_ids: Array.isArray(rule?.user_ids)
            ? rule.user_ids.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
            : [],
        }))
        .filter((rule) => rule.type)
    : [];

  return {
    incident_manager_ids: managers,
    notification_rules: rules,
  };
};

const statusColors = {
  submitted_pending_safety_review: 'orange',
  safety_review_completed: 'blue',
  investigation_in_progress: 'processing',
  investigation_submitted_to_safety: 'gold',
  investigation_approved: 'cyan',
  lessons_learned_pending_review: 'purple',
  distributed: 'geekblue',
  closed: 'green',
};

const IncidentList = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    totalCount: 0,
    pageCount: 0,
  });

  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activePermission = useSelector((state) => state.permission?.activePermission);
  const currentUserId = useSelector((state) => state.user?.user?.id);
  const canDecideApproval = ['owner', 'admin'].includes(activePermission);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState(searchInput);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [incidentSettings, setIncidentSettings] = useState({ ...DEFAULT_SETTINGS });
  const [manualNotificationRules, setManualNotificationRules] = useState(DEFAULT_MANUAL_NOTIFICATION_RULES);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scrollX, setScrollX] = useState(window.innerWidth <= 991 ? 1200 : undefined);

  const normalizedCurrentUserId = typeof currentUserId === 'number' ? currentUserId : Number(currentUserId);
  const incidentManagerIds = incidentSettings.incident_manager_ids || [];
  const isIncidentManager = !Number.isNaN(normalizedCurrentUserId) && incidentManagerIds.includes(normalizedCurrentUserId);
  const canManageSettings = ['owner', 'admin'].includes(activePermission);
  // Export is admin-only. Hiding the button is presentation only — the API
  // rejects a non-admin caller regardless.
  const canExport = ['owner', 'admin'].includes(activePermission);
  const canManageIncidents = ['owner', 'admin'].includes(activePermission) || isIncidentManager;

  const isSafetyAdmin = incidentSettings.is_safety_admin || ['owner', 'admin'].includes(activePermission);

  // Dynamic scroll based on viewport
  useEffect(() => {
    const handleResize = () => {
      setScrollX(window.innerWidth <= 991 ? 800 : 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);

    try {
      const response = await incidentService.getList({
        property_id: activePropertyId,
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearchInput,
        type: selectedType,
        status: selectedStatus,
      });

      setIncidents(response.items);
      setPagination(response._meta);
      setLoading(false);
    } catch (error) {
      console.error('There was an error fetching the incidents!', error);
      setLoading(false);
    }
  }, [activePropertyId, pagination.currentPage, pagination.perPage, debouncedSearchInput, selectedType, selectedStatus]);

  const handleExport = useCallback(async () => {
    if (!activePropertyId) return;
    setExporting(true);

    try {
      // Same filters as the table, minus pagination — the file carries every
      // matching incident, not just the page on screen.
      await incidentService.exportIncidents({
        property_id: activePropertyId,
        search: debouncedSearchInput,
        type: selectedType,
        status: selectedStatus,
      });
      message.success('Incidents exported');
    } catch (error) {
      const apiMessage = await readBlobErrorMessage(error);
      message.error(apiMessage || 'Failed to export incidents');
    } finally {
      setExporting(false);
    }
  }, [activePropertyId, debouncedSearchInput, selectedType, selectedStatus]);

  const actions = useIncidentActions({ onRefresh: fetchIncidents });

  const stats = {
    totalOpen: incidents.filter(i => i.status !== 'closed').length,
    myIncidents: incidents.filter(i => i.creator_id === normalizedCurrentUserId).length,
    underInvestigation: incidents.filter(i => i.status === 'investigation_in_progress').length,
    closed: incidents.filter(i => i.status === 'closed').length,
  };

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const fetchIncidentSettings = useCallback(async () => {
    if (!activePropertyId) {
      setIncidentSettings({ ...DEFAULT_SETTINGS });
      return;
    }
    setSettingsLoading(true);
    try {
      const response = await incidentService.getSettings(activePropertyId);
      const parsed = normalizeSettings(response?.settings || response || DEFAULT_SETTINGS);
      parsed.is_safety_admin = response?.is_safety_admin || false;
      setIncidentSettings(parsed);
    } catch (error) {
      console.error('Failed to load incident settings', error);
      setIncidentSettings({ ...DEFAULT_SETTINGS });
    } finally {
      setSettingsLoading(false);
    }
  }, [activePropertyId]);

  useEffect(() => {
    fetchIncidentSettings();
  }, [fetchIncidentSettings]);

  // The action menu is built from the property's rules rather than a fixed
  // "Notify MD" item. On failure it keeps the built-in default so the
  // escalation does not disappear from the menu.
  const fetchNotificationRules = useCallback(async () => {
    if (!activePropertyId) {
      setManualNotificationRules(DEFAULT_MANUAL_NOTIFICATION_RULES);
      return;
    }
    try {
      const response = await incidentService.getNotificationRules(activePropertyId);
      const rules = Array.isArray(response?.rules) ? response.rules : [];
      setManualNotificationRules(
        rules.filter((rule) => rule.trigger_event === 'manual' && rule.is_active)
      );
    } catch (error) {
      console.error('Failed to load incident notification rules', error);
      setManualNotificationRules(DEFAULT_MANUAL_NOTIFICATION_RULES);
    }
  }, [activePropertyId]);

  useEffect(() => {
    fetchNotificationRules();
  }, [fetchNotificationRules]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const handleCancelSearch = () => {
    setSearchInput("");
  };

  const handleTableChange = (paginationConfig, filters) => {
    setSelectedType(filters.type?.[0] ?? null);
    setSelectedStatus(filters.status?.[0] ?? null);
    setPagination({
      ...pagination,
      currentPage: paginationConfig.current,
      perPage: paginationConfig.pageSize,
    });
  };

  const handleEditClick = (incident) => {
    if (!canManageIncidents) {
      return;
    }
    actions.handleEditClick(incident);
  };

  const columns = [
    {
      title: <IntlMessages id="incidentPage.table.reportNumber" />,
      dataIndex: 'report_number',
      key: 'report_number',
      sorter: (a, b) => (a.report_number || '').localeCompare(b.report_number || ''),
      render: (text) => <strong>{text}</strong>,
      width: 150,
    },
    {
      title: <IntlMessages id="incidentPage.table.date" />,
      dataIndex: 'date_time',
      key: 'date_time',
      sorter: (a, b) => moment(a.date_time).unix() - moment(b.date_time).unix(),
      render: (text) => moment(text).format("DD/MM/YYYY HH:mm"),
      width: 150,
    },
    {
      title: <IntlMessages id="incidentPage.table.incidentType" />,
      dataIndex: 'type',
      key: 'type',
      filters: TYPES.map(t => ({ text: t.label, value: t.value })),
      filteredValue: selectedType ? [selectedType] : null,
      filterMultiple: false,
      render: (text) => {
        const typeOption = findIncidentTypeOption(text);
        return typeOption ? typeOption.label : text;
      },
      width: 150,
    },
    {
      title: <IntlMessages id="incidentPage.table.status" />,
      dataIndex: 'status',
      key: 'status',
      filters: STATUSES.map(s => ({ text: s.label, value: s.value })),
      filteredValue: selectedStatus ? [selectedStatus] : null,
      filterMultiple: false,
      render: (text, record) => (
        <Tag color={statusColors[text] || 'default'}>
          {record.status_label || text}
        </Tag>
      ),
      width: 180,
    },
    {
      title: 'Investigation',
      dataIndex: 'investigation_type',
      key: 'investigation_type',
      render: (text) => {
        if (!text) return '-';
        const invType = INVESTIGATION_TYPES.find((t) => t.value === text);
        return invType ? invType.label : text;
      },
      width: 130,
    },
    getApprovalColumn({
      canDecideApproval,
      onApprovalOpen: actions.handleApprovalOpen,
    }),
    getActionColumn({
      isSafetyAdmin,
      canManageIncidents,
      isOwner: activePermission === 'owner',
      currentUserId,
      notificationRules: manualNotificationRules,
      handlers: {
        ...actions,
        handleEditClick,
      },
    }),
  ];

  return (
    <LayoutWrapper>
      <IncidentPage>
        <Box id="containerBox">
          <div className="manage-property-header">
            <h2 className="manage-property-title" style={{ margin: 0 }}>
              <IntlMessages id="incidentPage.managePage.headingTitle" />
            </h2>
          </div>

          <Card
            className="holiday-card"
            title={
              <div className="card-header">
                <span className="card-title">Incident Management</span>
                <div className="card-actions">
                  <Input
                    placeholder="Search…"
                    className="incident-search-input"
                    suffix={
                      !searchInput && (
                        <SearchOutlined className="site-form-item-icon" />
                      )
                    }
                    prefix={
                      searchInput && (
                        <CloseCircleOutlined
                          onClick={handleCancelSearch}
                          className="site-form-item-cancel"
                        />
                      )
                    }
                    value={searchInput}
                    onChange={handleSearch}
                    style={{ width: 250, height: 40, marginRight: 8 }}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchIncidents}
                    loading={loading}
                    className="incident-refresh-button"
                    style={{ height: 40, borderRadius: 8, marginRight: 8 }}
                  >
                    Refresh
                  </Button>
                  {canExport && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleExport}
                      loading={exporting}
                      disabled={!activePropertyId}
                      className="incident-export-button"
                      style={{ height: 40, borderRadius: 8, marginRight: 8 }}
                    >
                      Export
                    </Button>
                  )}
                  {canManageSettings && (
                    <Tooltip title={<IntlMessages id="incidentPage.settings.title" />}>
                      <SettingsIconButton
                        shape="circle"
                        icon={<SettingOutlined />}
                        size="large"
                        onClick={() => setSettingsModalVisible(true)}
                        loading={settingsLoading}
                        disabled={!activePropertyId}
                      />
                    </Tooltip>
                  )}
                  <Button
                    type="primary"
                    className="btn-success"
                    icon={<PlusOutlined />}
                    onClick={actions.openCreateModal}
                  >
                    <IntlMessages id="incidentPage.managePage.addIncident" />
                  </Button>
                </div>
              </div>
            }
          >
            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Total Open"
                    value={stats.totalOpen}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="My Incidents"
                    value={stats.myIncidents}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: 'rgba(0, 0, 0, 0.85)' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Under Investigation"
                    value={stats.underInvestigation}
                    prefix={<AuditOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Closed"
                    value={stats.closed}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
              Showing {incidents.length} of {pagination.totalCount} incidents
            </div>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={incidents}
              loading={loading}
              rowKey="id"
              pagination={{
                current: pagination.currentPage,
                pageSize: pagination.perPage,
                total: pagination.totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} incidents`,
              }}
              onChange={handleTableChange}
              scroll={{ x: scrollX }}
              className="incident-table"
            />
          </Card>
        </Box>
      </IncidentPage>

      <IncidentActionModals
        {...actions}
        isSafetyAdmin={isSafetyAdmin}
        currentUserId={currentUserId}
        canDecideApproval={canDecideApproval}
        onRefresh={fetchIncidents}
        onApprovalUpdated={(incidentId, approval) => {
          setIncidents((prev) =>
            prev.map((incident) =>
              incident.id === incidentId
                ? { ...incident, latest_approval: approval }
                : incident
            )
          );
        }}
      />

      <IncidentSettingsModal
        visible={settingsModalVisible}
        propertyId={activePropertyId}
        onCancel={() => setSettingsModalVisible(false)}
        onSaved={(settings) => {
          setIncidentSettings(normalizeSettings(settings));
          fetchNotificationRules();
          setSettingsModalVisible(false);
        }}
      />
    </LayoutWrapper>
  );
};

export default IncidentList;
